<template>
  <div ref="containerEl" :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <div class="stats-bar-list">
      <div class="stats-bar-item" v-for="item in fields?.list" :key="item.id">
        <FlippedNumber
          v-if="isInView && getPlainNumber(item)"
          :key="`${item.id}-${animationCycle}`"
          :number="getPlainNumber(item)"
          class="stats-bar-item-number"
        />
        <JssRichText :field="item?.fields?.desc" class="stats-bar-item-desc" />
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import utils from '@/utils';

export default {
  name: 'StatsBar',
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    rendering: {
      type: Object,
    },
  },
  setup(props) {
    const containerEl = ref(null);
    const isInView = ref(false);
    const animationCycle = ref(0);
    let visibilityObserver = null;

    const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'stats-bar'));

    const rootStyle = computed(() => {
      const result = utils.overall.baseComStyle(props.fields);
      if (props.fields?.backgroundColor?.value) {
        result.backgroundColor = props.fields.backgroundColor.value;
      }
      return result;
    });

    const getPlainNumber = (item) => {
      const raw = item?.fields?.number?.value;
      if (!raw) return '';
      return String(raw)
        .replace(/<[^>]*>/g, '')
        .trim();
    };

    onMounted(() => {
      if (!containerEl.value || !window.IntersectionObserver) {
        isInView.value = true;
        animationCycle.value += 1;
        return;
      }

      visibilityObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            animationCycle.value += 1;
            isInView.value = true;
          } else {
            isInView.value = false;
          }
        },
        { threshold: 0.1 }
      );
      visibilityObserver.observe(containerEl.value);
    });

    onBeforeUnmount(() => {
      visibilityObserver?.disconnect();
    });

    return {
      containerEl,
      isInView,
      animationCycle,
      rootClass,
      rootStyle,
      getPlainNumber,
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.stats-bar {
  position: relative;
  width: 100%;
  padding: 20px calc(100vw / 16);

  &-list {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
  }

  &-item {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;

    &-number {
      font-size: 30px;
      line-height: 1.2;
      font-weight: 700;
      color: $white;

      .e-flipped-number__card {
        max-height: none;
      }
    }

    &-desc {
      font-size: 16px;
      line-height: 1.4;
      color: $white;
      text-align: center;
    }
  }

  @include tablet-landscape {
    padding: 45px 60px;

    &-list {
      flex-direction: row;
    }

    &-item {
      flex: 1;
      align-items: center;
      border-right: 1px solid #8fa690;
      padding: 0 60px;
      &:last-child {
        border-right: none;
      }
      &-number {
        font-size: 44px;
      }

      &-desc {
        text-align: center;
      }
    }
  }
}
</style>
